/**
 * El rastro de la prueba: qué etapas recorrió el agente mientras lo probabas, y en cuál se quedó.
 *
 * DE DÓNDE SALE LA IDEA. n8n, cuando ejecutás un flujo a mano, no te deja adivinando: pinta el
 * resultado ENCIMA del lienzo. Cada nodo que corrió bien queda con un tilde verde; el que falló
 * queda marcado en rojo y al abrirlo ves el error y los datos de entrada que lo rompieron; y el que
 * corrió pero cuya salida ya quedó vieja se marca aparte («dirty node»). El lienzo deja de ser un
 * dibujo de lo que debería pasar y pasa a ser el registro de lo que pasó.
 *
 * Acá el equivalente del nodo es la ETAPA del embudo. El founder lo pidió con estas palabras:
 * «sería bueno ver en qué etapa se queda y qué falla exactamente».
 *
 * 🚨 EL DATO YA EXISTÍA Y NADIE LO MIRABA. `agent-test-chat` devuelve en cada turno `recorrido`,
 * `etapa` y `tool_calls` —los mismos que el libro de a bordo escribe en producción—, pero se
 * mostraban como texto chiquito en el encabezado del chat y se perdían al turno siguiente. Este
 * contexto los ACUMULA y los publica al constructor, que es donde está dibujado el embudo. No hay
 * telemetría nueva: hay el mismo dato, puesto donde se puede leer.
 *
 * 🪤 POR QUÉ UN CONTEXTO APARTE Y NO UN CAMPO MÁS DEL DOCK. El rastro cambia en CADA turno. Si
 * viviera en [WaChatDockContext], cada respuesta del agente volvería a renderizar las tres ventanas
 * de chat abiertas —incluidas las de clientes reales, con su lista de mensajes y su borrador— por
 * un dato que solo le importa al constructor. Se monta adentro del provider del dock para no tocar
 * el árbol de [AppLayout], pero es un contexto separado a propósito.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** Un turno de la prueba, visto desde el embudo. */
export interface PasoDelRastro {
  /** 1, 2, 3… el número de turno dentro de esta prueba. */
  turno: number;
  /** Qué recorrido eligió el orquestador (`venta`, `recompra`, `postventa`…). */
  recorrido?: string;
  /** En qué etapa quedó la conversación después de este turno. */
  etapa?: string;
  /** Qué herramientas consultó para responder. Es el «input data» del nodo, en n8n. */
  herramientas: string[];
  /** Si el turno se cayó: por qué. Vacío si respondió bien. */
  error?: string;
}

/** Cómo se pinta una etapa del embudo según lo que pasó en la prueba. */
export type EstadoDeEtapa = 'aqui' | 'paso' | 'fallo' | 'sin_visitar';

export interface RastroDePrueba {
  agenteId: string;
  pasos: PasoDelRastro[];
  /** Hay un turno en vuelo: el agente está pensando. */
  pensando: boolean;
}

interface RastroApi {
  rastro: RastroDePrueba | null;
  /** Marca que arrancó un turno (para el pulso de «pensando»). */
  arrancoUnTurno: (agenteId: string) => void;
  /** Anota el resultado del turno. */
  anotarPaso: (agenteId: string, paso: Omit<PasoDelRastro, 'turno'>) => void;
  /** Borra el rastro (al reiniciar la prueba o cerrar la ventana). */
  olvidar: () => void;
}

const Ctx = createContext<RastroApi | null>(null);

export function RastroDePruebaProvider({ children }: { children: ReactNode }) {
  const [rastro, setRastro] = useState<RastroDePrueba | null>(null);

  const arrancoUnTurno = useCallback((agenteId: string) => {
    setRastro((prev) => (
      prev && prev.agenteId === agenteId
        ? { ...prev, pensando: true }
        : { agenteId, pasos: [], pensando: true }
    ));
  }, []);

  const anotarPaso = useCallback((agenteId: string, paso: Omit<PasoDelRastro, 'turno'>) => {
    setRastro((prev) => {
      const base = prev && prev.agenteId === agenteId ? prev : { agenteId, pasos: [], pensando: false };
      return { ...base, pensando: false, pasos: [...base.pasos, { ...paso, turno: base.pasos.length + 1 }] };
    });
  }, []);

  const olvidar = useCallback(() => setRastro(null), []);

  const api = useMemo(() => ({ rastro, arrancoUnTurno, anotarPaso, olvidar }), [rastro, arrancoUnTurno, anotarPaso, olvidar]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/** 🪤 Inerte sin provider, igual que el dock: una pantalla fuera del shell no puede caerse por esto. */
export function useRastroDePrueba(): RastroApi {
  return useContext(Ctx) ?? { rastro: null, arrancoUnTurno: () => {}, anotarPaso: () => {}, olvidar: () => {} };
}

/**
 * Cómo se pinta cada etapa, dado el rastro.
 *
 * 🪤 «PASÓ POR ACÁ» NO ES «ESTUVO ACÁ EN ALGÚN TURNO». El embudo no retrocede: si la conversación
 * está en `confirmar`, pasó por `descubrir` aunque ningún turno haya quedado registrado ahí —un
 * cliente que manda «quiero 2 del rojo, soy de Huancayo» salta tres etapas en un mensaje. Por eso
 * se marca como cumplida toda etapa ANTERIOR a la actual en el orden del recorrido, no solo las que
 * aparecen literalmente en la lista de pasos. Mirar solo lo literal deja el embudo lleno de huecos
 * y hace ver como saltada una etapa que en realidad se resolvió sola.
 */
export function estadosDelRastro(
  rastro: RastroDePrueba | null,
  recorridoId: string,
  etapasEnOrden: string[],
): Record<string, EstadoDeEtapa> {
  const salida: Record<string, EstadoDeEtapa> = {};
  for (const id of etapasEnOrden) salida[id] = 'sin_visitar';
  if (!rastro || rastro.pasos.length === 0) return salida;

  // Solo miran a este recorrido los turnos que corrieron en él.
  const propios = rastro.pasos.filter((p) => p.recorrido === recorridoId);
  if (propios.length === 0) return salida;

  const ultimo = propios[propios.length - 1];
  const iActual = ultimo.etapa ? etapasEnOrden.indexOf(ultimo.etapa) : -1;

  for (const p of propios) {
    if (p.etapa && salida[p.etapa] !== undefined) salida[p.etapa] = 'paso';
  }
  if (iActual >= 0) {
    for (let i = 0; i < iActual; i++) salida[etapasEnOrden[i]] = 'paso';
    salida[etapasEnOrden[iActual]] = ultimo.error ? 'fallo' : 'aqui';
  }
  return salida;
}

/** Qué se vio en una etapa durante la prueba: para el detalle al abrir la ficha. */
export function pasosDeEtapa(rastro: RastroDePrueba | null, recorridoId: string, etapaId: string): PasoDelRastro[] {
  return (rastro?.pasos ?? []).filter((p) => p.recorrido === recorridoId && p.etapa === etapaId);
}
